import { memo, ReactNode, useState } from 'react'

import { Edit } from '@mui/icons-material'
import { Box, Button, Paper, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { DetailRow } from '@shared/components/DetailRow'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import {
  formatContentLifecycleDuration,
  StreamKind,
  StreamThumbnail,
  useStreamDetail,
} from 'domains/Workspaces/Streams'
import { Section } from 'domains/Workspaces/Streams/AvcTaskAssociations'
import { GeneralForm } from 'domains/Workspaces/Streams/General/GeneralForm'
import { RefetchIntervals } from 'types/query'

export type GeneralProps = {
  streamKind: StreamKind
  streamId: string
  /** Wraps each linked-task row in a link to its AVC task. Routes differ per stream kind. */
  TaskLink: (taskId: string, content: ReactNode) => ReactNode
  /** The live/ended pill, which is a per-kind styled component. */
  StateSlot: (isLive: boolean) => ReactNode
}

const GeneralRaw = ({ streamKind, streamId, TaskLink, StateSlot }: GeneralProps) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])

  const [editing, setEditing] = useState(false)

  const { stream, isLoading, save, isSaving } = useStreamDetail({
    streamKind,
    streamId,
    // Polling would reset the form's captured defaults under the user's cursor.
    refetchInterval: editing ? false : RefetchIntervals.NORMAL,
  })

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (!stream) {
    return <NotFound />
  }

  const lifecycle = formatContentLifecycleDuration(stream.contentLifecyclePolicy.deleteAfter)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 4, flexGrow: 1, minHeight: 0 }}>
      {editing ? (
        <Box sx={{ flexGrow: 1, minHeight: 0, bgcolor: 'background.levels.100', borderRadius: 1, overflow: 'hidden' }}>
          <GeneralForm
            stream={stream}
            save={save}
            isSaving={isSaving}
            onSaved={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 4, flexShrink: 0, pb: 4 }}>
          {/* `columnGap` rather than Stack `spacing`, whose `& > * + *` margin-left rule
              out-specifies the Edit button's `ml: 'auto'` and would keep it off the right edge. */}
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
            <StreamThumbnail streamKind={streamKind} url={stream.thumbnail?.url} alt={stream.name || stream.id} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="heading-lg" noWrap>
                {stream.name || t('noNameProvided')}
              </Typography>
              <Box sx={{ mt: 1 }}>{StateSlot(!!stream.live)}</Box>
            </Box>
            {/* `userRights` is omitted by the API in the common case, so only an explicit `false` hides Edit. */}
            {stream.userRights?.canEdit !== false && (
              <Button sx={{ ml: 'auto' }} startIcon={<Edit />} variant="outlined" onClick={() => setEditing(true)}>
                {t('edit')}
              </Button>
            )}
          </Box>

          <Paper
            sx={{
              px: 4,
              py: 3,
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 6,
              rowGap: 2,
              '& > *': { minWidth: 180, maxWidth: 320 },
            }}
            elevation={0}
          >
            <DetailRow label={t('id')} value={stream.id} />
            <DetailRow label={t('type')} value={stream.type} />
          </Paper>

          <Paper sx={{ px: 4, py: 3, maxWidth: 480 }} elevation={0}>
            <Typography sx={{ mb: 1 }} variant="heading-md">
              {t('domain:Workspace.Streams.contentLifecycle')}
            </Typography>
            <Typography variant="heading-sm">
              {lifecycle ? lifecycle.label : t('domain:Workspace.Streams.lifecycleNoExpiry')}
            </Typography>
            <Typography sx={{ color: 'text.secondary' }} variant="text-sm">
              {lifecycle
                ? t('domain:Workspace.Streams.lifecycleDeletion', { time: lifecycle.full })
                : t('domain:Workspace.Streams.lifecycleNoDeletion')}
            </Typography>
          </Paper>

          <Section associations={stream.avcTaskAssociations} TaskLink={TaskLink} />
        </Box>
      )}
    </Box>
  )
}

export const General = memo(GeneralRaw)
